import React, { FC } from 'react';
import Form from 'components/Form/Form';
import { FormItem } from 'components/Form/FormItem';
import { Input } from 'components/Input';
import Upload from 'components/ImageUpload/ImageUpload';
import Select from 'antd/lib/select';
import Button from 'components/Button/Button';
import Alert from 'components/Alert/Alert';
import { FormInstance } from 'antd/es/form/Form';
import { ECountryUI, TOrganizationDataUi } from 'data/types/organization.types';
import { ArrowRight } from 'pages/public/authFlow/icons';
import TimeZone from '../TimeZone/TimeZone';
import WorkingHoursField from '../WorkingHoursField/WorkingHoursField';
import { companySize } from '../static/static';

type TOrganizationStepProps = {
  form: FormInstance<any>;
  initialValues?: TOrganizationDataUi;
  error?: string;
  onNext: () => void;
  nextLoading?: boolean;
};

const countryOptions = Object.values(ECountryUI).map(c => ({
  label: c,
  value: c,
}));

// Default MON–FRI 08:00–17:00 working hours.
const DEFAULT_WORKING_HOURS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
].map(dayOfWeek => ({ dayOfWeek, start: '08:00:00', end: '17:00:00' }));

type TLogoUploadProps = {
  /** Injected by the antd FormItem (`logoUrl`). */
  value?: string;
  onChange?: (url: string) => void;
};

/** New-theme logo uploader — reuses the shared ImageUpload (real upload +
 *  onChange(url)) rendered as the round avatar, with the design's title/hint. */
const LogoUpload: FC<TLogoUploadProps> = ({ value, onChange }) => (
  <div className="onb__logo">
    <Upload
      viewLabel={false}
      width={64}
      height={64}
      value={value}
      onChange={url => onChange?.(url)}
    />
    {/* Same htmlFor as ImageUpload's input so the text is a click target too. */}
    <label htmlFor="image" className="onb__logo-text">
      <div className="onb__logo-title">Upload organization logo</div>
      <div className="onb__logo-hint">PNG or JPG, up to 10MB.</div>
    </label>
  </div>
);

/** Onboarding step 1 — Organization details (ZEST-738 redesign). */
const OrganizationStep: FC<TOrganizationStepProps> = ({
  form,
  initialValues,
  error,
  onNext,
  nextLoading,
}) => (
  <>
    <div className="onb__header">
      <h2 className="onb__title">Organization details</h2>
      <p className="onb__subtitle">
        Provide your company baseline configuration details below to spin up
        your instance profile.
      </p>
    </div>

    <Alert type="error" message={error} />

    <Form
      form={form}
      name="organization"
      initialValues={{ workingHours: DEFAULT_WORKING_HOURS, ...initialValues }}
      onFinish={onNext}
    >
      <div className="onb__fields">
        <FormItem name="logoUrl">
          <LogoUpload />
        </FormItem>

        <FormItem
          name="name"
          label="Company Name"
          rules={[{ required: true, message: 'This field is required' }]}
        >
          <Input placeholder="e.g Apex Tech Solutions" />
        </FormItem>

        <FormItem
          name="website"
          label="Company Website"
          rules={[{ required: true, message: 'This field is required' }]}
        >
          <Input placeholder="https://apextech.com" />
        </FormItem>

        <FormItem
          name="address"
          label="Company Address"
          rules={[{ required: true, message: 'This field is required' }]}
        >
          <Input placeholder="123 Technology Drive, Suite 400" />
        </FormItem>

        <FormItem name="address2" label="Suite / Floor">
          <Input placeholder="Suite / floor (optional)" />
        </FormItem>

        <div className="onb__row">
          <FormItem
            name="country"
            label="Company Location"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Select
              placeholder="Select country"
              options={countryOptions}
              onChange={() => form.setFieldsValue({ timeZone: undefined })}
            />
          </FormItem>
          <FormItem
            name="timeZone"
            label="Select Timezone"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <TimeZone />
          </FormItem>
        </div>

        <FormItem
          name="size"
          label="Company Size"
          rules={[{ required: true, message: 'This field is required' }]}
        >
          <Select placeholder="Select company size" options={companySize} />
        </FormItem>
      </div>

      <div className="onb__divider" />

      <div>
        <h3 className="onb__section-title">Set working hours</h3>
        <p className="onb__section-desc">
          Configure your operational days and hours for MSP ticket routing.
        </p>
        <FormItem
          name="workingHours"
          rules={[
            {
              validator: (_, val) =>
                val && val.length
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error('Set working hours for at least one day'),
                    ),
            },
          ]}
        >
          <WorkingHoursField />
        </FormItem>
      </div>

      <div className="onb__footer">
        <div className="onb__next">
          <Button type="primary" htmlType="submit" loading={nextLoading}>
            Next Step&nbsp;
            <ArrowRight />
          </Button>
        </div>
      </div>
    </Form>
  </>
);

export default OrganizationStep;
